import { bridge, BRIDGE_METHODS } from '@h5-journey/bridge'
import { reportEventMiniProgram } from '@h5-journey/service'
import { ATOMIC_SERVICE_TRACK_SOURCE, getUniqueId, isAtomicService, isInWeChatMiniProgram } from '@h5-journey/utils'
import type { TrackOptions, TrackParamInput, TrackParams } from './type'
import { trackSource, resolveTrackSource } from './constant'
import { getTrackChannelParams } from './channelSource'

class Analytics {
  static async trackPageView(
    eventName: string,
    params: TrackParamInput = null,
    options: TrackOptions = {}
  ) {
    const { timeout = 3000, retry = true, maxRetries = 2 } = options

    try {
      console.log(params)

      const resolvedParams = await this.resolveParams(params, timeout)
      await this.reportEvent(eventName, resolvedParams, { retry, maxRetries })
    } catch (error) {
      console.error(`页面曝光埋点失败`, error)
    }
  }

  static async trackEvent(
    eventName: string,
    params: TrackParamInput = null,
    options: TrackOptions = {}
  ) {
    const { timeout = 3000, retry = true, maxRetries = 2 } = options

    try {
      const resolvedParams = await this.resolveParams(params, timeout)
      await this.reportEvent(eventName, resolvedParams, { retry, maxRetries })
    } catch (error) {
      console.error(`按钮点击埋点失败: ${eventName}`, error)
    }
  }

  static async trackWithApiCall(
    eventName: string,
    apiCall: () => Promise<unknown>,
    paramsExtractor: ((result: unknown) => TrackParams | null | undefined) | null = null,
    options: TrackOptions = {}
  ) {
    try {
      const apiResult = await apiCall()

      let params: TrackParams = {}
      if (paramsExtractor && typeof paramsExtractor === 'function') {
        try {
          params = paramsExtractor(apiResult) || {}
        } catch (error) {
          console.warn('参数提取失败:', error)
        }
      }

      await this.trackEvent(eventName, params, options)

      return apiResult
    } catch (error) {
      console.error(`接口调用失败: ${eventName}`, error)
      throw error
    }
  }

  static async resolveParams(params: TrackParamInput, timeout: number): Promise<TrackParams> {
    if (!params) return {}
    try {
      let resolvedParams: unknown
      if (typeof params === 'function') {
        resolvedParams = await Promise.race([
          params(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('参数获取超时')), timeout))
        ])
      } else if (params instanceof Promise) {
        resolvedParams = await Promise.race([
          params,
          new Promise((_, reject) => setTimeout(() => reject(new Error('参数获取超时')), timeout))
        ])
      } else {
        resolvedParams = params
      }
      return typeof resolvedParams === 'object' && resolvedParams !== null
        ? (resolvedParams as TrackParams)
        : {}
    } catch (error) {
      console.warn('参数解析失败，使用空对象:', error)
      return {}
    }
  }

  static getAppBaseParams() {
    return {
      source: 'app'
    }
  }

  static getAtomicServiceBaseParams() {
    return {
      source: ATOMIC_SERVICE_TRACK_SOURCE,
      timestamp: Date.now(),
      cuid: getUniqueId('track-uuid'),
      ...getTrackChannelParams()
    }
  }

  static getWeChatMiniProgramBaseParams() {
    try {
      const baseParamsStr = sessionStorage.getItem('wxappBaseParams')
      if (baseParamsStr) {
        const allParams = JSON.parse(baseParamsStr)
        const {
          version = '',
          osBrand = '',
          osVersion = '',
          cuid = '',
          province = '',
          city = '',
          district = '',
          longitude = '',
          latitude = '',
          timestamp = ''
        } = allParams

        return {
          source: 'wxapp',
          version,
          osBrand,
          osVersion,
          cuid,
          province,
          city,
          district,
          longitude,
          latitude,
          timestamp
        }
      }
    } catch (error) {
      console.warn('获取微信小程序基础参数失败:', error)
    }
    return {
      source: 'wxapp',
      timestamp: ''
    }
  }

  static async reportEvent(
    eventName: string,
    params: TrackParams = {},
    options: TrackOptions = {}
  ) {
    console.log('埋点参数：', params)
    const { retry = true, maxRetries = 2 } = options
    let retryCount = 0

    const attemptReport = async (): Promise<unknown> => {
      try {
        if (isInWeChatMiniProgram()) {
          const baseParams = this.getWeChatMiniProgramBaseParams()
          return await reportEventMiniProgram({
            ...baseParams,
            ...getTrackChannelParams(),
            ...params
          })
        }

        const hasFlutterBridge =
          typeof window !== 'undefined' &&
          !!(window as Window & { flutter_inappwebview?: unknown }).flutter_inappwebview
        if (hasFlutterBridge) {
          const baseParams = this.getAppBaseParams()
          return await bridge.invokeSync(eventName, {
            ...baseParams,
            ...getTrackChannelParams(),
            ...params
          })
        }

        if (isAtomicService()) {
          const baseParams = this.getAtomicServiceBaseParams()
          return await reportEventMiniProgram({
            ...baseParams,
            ...params
          })
        }

        // 普通 H5 环境：source 取自定义值（小程序/APP/元服务已在上面分支强制覆盖）
        const p = {
          source: resolveTrackSource(trackSource),
          timestamp: Date.now(),
          cuid: getUniqueId(`track-uuid`),
          ...getTrackChannelParams(),
          ...params
        }
        return await reportEventMiniProgram(p)
      } catch (error) {
        if (retry && retryCount < maxRetries) {
          retryCount++
          await new Promise((resolve) => setTimeout(resolve, Math.pow(2, retryCount) * 1000))
          return attemptReport()
        }
        console.log(error)
      }
    }

    return attemptReport()
  }

  static async trackBatch(
    events: { name?: string; params?: TrackParamInput; options?: TrackOptions }[]
  ) {
    const results = []

    for (const event of events) {
      try {
        const { name, params, options } = event
        const result = await this.trackEvent(name as string, params ?? null, options ?? {})
        results.push({ name, success: true, result })
      } catch (error) {
        results.push({ name: event.name, success: false, error: (error as Error).message })
      }
    }

    return results
  }
}

export { Analytics }

export const trackPageView = Analytics.trackPageView.bind(Analytics)
export const trackEvent = Analytics.trackEvent.bind(Analytics)

/**
 * 上报 TCN 自建埋点事件（V2 推荐入口）。
 *
 * App 端固定调用 `internalAnalysis`；微信小程序和普通 H5 仍由公共层分流至
 * `/stat/mark`。业务代码无需传入 Flutter Bridge 方法名。
 */
export const trackEventV2 = (
  params: TrackParamInput = null,
  options: TrackOptions = {}
) => Analytics.trackEvent(BRIDGE_METHODS.SEND_TRACK_INTERNAL, params, options)

/**
 * 上报 TCN 自建页面/曝光埋点（V2 推荐入口）。
 *
 * 与 `trackEventV2` 使用相同的多端分流，区别仅为调用语义。
 */
export const trackPageViewV2 = (
  params: TrackParamInput = null,
  options: TrackOptions = {}
) => Analytics.trackPageView(BRIDGE_METHODS.SEND_TRACK_INTERNAL, params, options)

export const trackWithApiCall = Analytics.trackWithApiCall.bind(Analytics)
export const trackBatch = Analytics.trackBatch.bind(Analytics)
